"use client";

import { ArrowRight, Check, Mail } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import posthog from "posthog-js";
import {
  type FormEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ReaderReportType } from "@/lib/reports/report-type";
import type { LandingPagePreviewData } from "../_shared/landing-preview-data";
import type {
  ExploreCategory,
  TrendingTag,
} from "../[lang]/[reportType]/explore/types";

// ============================================================
// Types
// ============================================================

type Edition = "engineer" | "creative";

interface UnifiedLandingPageProps {
  engineerData: LandingPagePreviewData;
  creativeData: LandingPagePreviewData;
  defaultEdition?: Edition;
}

interface EditionConfig {
  exploreHubPath: string;
  explorePathPrefix: string;
  tagPathPrefix: string;
  headline: ReactNode;
  subtitle: ReactNode;
  valuePills: string[];
  audienceLine: string;
  emailPreview: string;
  showcaseDesktop: string;
  showcaseMobile: string;
  showcaseDetail: string;
  storyCards: { src: string; alt: string }[];
  subscribeAudience: ReaderReportType[];
  data: LandingPagePreviewData;
}

// ============================================================
// Constants
// ============================================================

const SANS = { fontFamily: "var(--homepage-sans-stack)" } as const;
const TITLE = { fontFamily: "var(--explore-card-title-stack)" } as const;
const X_LINK = "https://x.com/aidailyprimer";
const TELEGRAM_LINK = "https://t.me/aidailyprimer";
const ACCENT = "var(--explore-accent-orange)";
const BUTTON_COLOR = "#FFC002";

const HIGHLIGHT_YELLOW = {
  backgroundColor: "var(--explore-highlight-yellow)",
  borderRadius: "2px",
  padding: "0 3px",
} as const;

const HIGHLIGHT_PEACH = {
  backgroundColor: "var(--explore-highlight-peach)",
  borderRadius: "2px",
  padding: "0 3px",
} as const;

const HIGHLIGHT_MINT = {
  backgroundColor: "var(--explore-highlight-mint)",
  borderRadius: "2px",
  padding: "0 3px",
} as const;

// ============================================================
// Keyframes — injected once at root
// ============================================================

const GLOBAL_KEYFRAMES = `
  @keyframes btn-glow {
    0%, 100% { box-shadow: 0 0 4px 0 rgba(255, 192, 2, 0.6); }
    50% { box-shadow: 0 0 24px 8px rgba(255, 192, 2, 0.45); }
  }
  @keyframes wiggle {
    0%, 100% { transform: translateX(0); }
    10% { transform: translateX(-4px) rotate(-1deg); }
    20% { transform: translateX(4px) rotate(1deg); }
    30% { transform: translateX(-3px) rotate(-0.5deg); }
    40% { transform: translateX(3px) rotate(0.5deg); }
    50% { transform: translateX(-2px); }
    60% { transform: translateX(2px); }
    70% { transform: translateX(0); }
  }
  @keyframes tag-editorial {
    0%, 100% { transform: scale(1) rotate(0); z-index: 1; filter: none; }
    2% { transform: translateY(-5px) scale(1.06) rotate(-1deg); z-index: 50; filter: drop-shadow(0 6px 16px rgba(0,0,0,0.10)); }
    3.5% { transform: translateY(-5px) scale(1.06) rotate(0.5deg); z-index: 50; filter: drop-shadow(0 6px 16px rgba(0,0,0,0.10)); }
    5% { transform: scale(1) rotate(0); z-index: 1; filter: none; }
  }
  @keyframes card-float {
    0%, 100% { transform: translateY(0) rotate(var(--card-rotate)); }
    50% { transform: translateY(-8px) rotate(var(--card-rotate)); }
  }
  @keyframes phone-scroll {
    0%, 12% { transform: translateY(0); }
    44%, 56% { transform: translateY(-30%); }
    88%, 100% { transform: translateY(0); }
  }
`;

// ============================================================
// Edition configs
// ============================================================

function buildEditionConfigs({
  engineerData,
  creativeData,
}: UnifiedLandingPageProps): Record<Edition, EditionConfig> {
  return {
    engineer: {
      exploreHubPath: "/",
      explorePathPrefix: "/engineer",
      tagPathPrefix: "/engineer/tags",
      headline: (
        <>
          The AI changelog for
          <br />
          <span style={HIGHLIGHT_YELLOW}>AI Engineers</span>
        </>
      ),
      subtitle: (
        <>
          New models drop. Agent tools ship. Someone figures out a better
          workflow. It{"\u2019"}s scattered across X, Reddit, and Hacker News.
          We pull it together into 5{"\u00a0"}stories and 30{"\u00a0"}briefs you
          can scan in 5{"\u00a0"}minutes.
        </>
      ),
      valuePills: [
        "Fresh News",
        "Skills",
        "Repos",
        "Official Releases",
        "Community Discussions",
        "Workflows",
      ],
      audienceLine:
        "For engineers, developers, entrepreneurs, marketing professionals",
      emailPreview: "/email-preview-engineer.png",
      showcaseDesktop: "/landing/engineer-showcase-desktop.webp",
      showcaseMobile: "/landing/engineer-showcase-mobile.webp",
      showcaseDetail: "/landing/engineer-showcase-detail.png",
      storyCards: [
        {
          src: "/landing/engineer-story-card-1.png",
          alt: "Anthropic limits Claude 5-hour sessions during 5am-11am PT",
        },
        {
          src: "/landing/engineer-story-card-2.png",
          alt: "Gemini 3.1 Flash Live launches with 90.8% audio tool-use",
        },
        {
          src: "/landing/engineer-story-card-3.png",
          alt: "Codex launches plugins for Slack, Figma, Gmail, and Google",
        },
      ],
      subscribeAudience: ["engineer"],
      data: engineerData,
    },
    creative: {
      exploreHubPath: "/creative",
      explorePathPrefix: "/creative",
      tagPathPrefix: "/creative/tags",
      headline: (
        <>
          Explore, create,
          <br />
          <span style={HIGHLIGHT_YELLOW}>play with AI</span>
        </>
      ),
      subtitle: (
        <>
          Discover the latest AI creative tools and start using them today.
          Create faster, experiment boldly, stay ahead of everyone.
        </>
      ),
      valuePills: [
        "Fresh News",
        "Skills",
        "Prompts",
        "Cutting-Edge Pipelines",
        "Workflows",
        "Community Discussions",
      ],
      audienceLine:
        "For creatives, art directors, designers, producers, filmmakers, bloggers, writers, artists",
      emailPreview: "/email-preview-creative.png",
      showcaseDesktop: "/landing/creative-showcase-desktop.webp",
      showcaseMobile: "/landing/creative-showcase-mobile.webp",
      showcaseDetail: "/landing/creative-showcase-detail.png",
      storyCards: [
        {
          src: "/landing/creative-story-card-1.png",
          alt: "Runway launches Multi-Shot App on web for prompt-to-video",
        },
        {
          src: "/landing/creative-story-card-2.png",
          alt: "Nano Banana 2 reportedly adds 4K image output",
        },
        {
          src: "/landing/creative-story-card-3.png",
          alt: "UNI-1 supports text-to-manga and Pouty Pal workflows",
        },
      ],
      subscribeAudience: ["creative"],
      data: creativeData,
    },
  };
}

function Animated({
  children,
  className = "",
  delay = 0,
}: {
  children: ReactNode;
  className?: string;
  delay?: number;
}) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => setIsVisible(true), delay);
    return () => window.clearTimeout(timeoutId);
  }, [delay]);

  return (
    <div
      className={`transition-all duration-700 ease-out ${
        isVisible ? "translate-y-0 opacity-100" : "translate-y-5 opacity-0"
      } ${className}`}
    >
      {children}
    </div>
  );
}

// ============================================================
// Parallax — scroll-driven depth layer
// ============================================================

function Parallax({
  children,
  speed = 0.1,
  className = "",
  style: externalStyle,
}: {
  children: ReactNode;
  speed?: number;
  className?: string;
  style?: React.CSSProperties;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (ticking) {
        return;
      }
      ticking = true;
      requestAnimationFrame(() => {
        if (!ref.current) {
          ticking = false;
          return;
        }
        const rect = ref.current.getBoundingClientRect();
        const viewH = window.innerHeight;
        const progress = 1 - (rect.top + rect.height) / (viewH + rect.height);
        setOffset((progress - 0.5) * speed * viewH);
        ticking = false;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [speed]);

  return (
    <div className={className} ref={ref} style={externalStyle}>
      <div
        style={{
          transform: `translateY(${offset}px)`,
          willChange: "transform",
        }}
      >
        {children}
      </div>
    </div>
  );
}

// ============================================================
// Edition Toggle
// ============================================================

function EditionToggle({
  edition,
  onToggle,
}: {
  edition: Edition;
  onToggle: (edition: Edition) => void;
}) {
  const [shouldWiggle, setShouldWiggle] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => setShouldWiggle(false), 3000);
    return () => clearTimeout(t);
  }, []);

  return (
    <div
      className="inline-flex items-center rounded-full border-none bg-[var(--explore-bg-card)] p-1 shadow-sm outline-none"
      style={{
        animation: shouldWiggle ? "wiggle 0.8s ease-in-out 0.5s 2" : "none",
      }}
    >
      {(["engineer", "creative"] as const).map((ed) => (
        <button
          className={`cursor-pointer rounded-full px-5 py-2 font-semibold text-sm outline-none transition-all sm:px-5 sm:py-2 sm:text-sm ${
            edition === ed
              ? "text-[var(--explore-on-accent-text)] shadow-sm"
              : "text-[var(--explore-text-muted)] hover:text-[var(--explore-text-primary)]"
          }`}
          key={ed}
          onClick={() => onToggle(ed)}
          style={{
            ...SANS,
            backgroundColor: edition === ed ? BUTTON_COLOR : "transparent",
          }}
          type="button"
        >
          {ed === "engineer" ? "For Engineers" : "For Creators"}
        </button>
      ))}
    </div>
  );
}

// ============================================================
// Subscribe Form (inline email input)
// ============================================================

interface SubscribeResponse {
  message?: string;
  alreadySubscribed?: boolean;
  error?: string;
}

function SubscribeForm({
  source,
  audience,
  otherEditionLabel,
}: {
  source: string;
  audience: ReaderReportType[];
  otherEditionLabel: string;
}) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<
    "idle" | "submitting" | "success" | "error"
  >("idle");
  const [message, setMessage] = useState("");
  const [alreadySubscribed, setAlreadySubscribed] = useState(false);
  const [alsoOther, setAlsoOther] = useState(false);

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const trimmed = email.trim();
      if (!trimmed) {
        return;
      }

      setStatus("submitting");
      const finalAudience: ReaderReportType[] = alsoOther
        ? ["engineer", "creative"]
        : audience;

      try {
        const res = await fetch("/api/subscribe", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: trimmed,
            source,
            audience: finalAudience,
            posthogSessionId: posthog.get_session_id?.(),
          }),
        });
        const data: SubscribeResponse = await res.json();
        if (res.ok) {
          setStatus("success");
          setMessage(data.message ?? "");
          setAlreadySubscribed(data.alreadySubscribed ?? false);
          setEmail("");
          return;
        }
        setStatus("error");
        setMessage(data.error ?? "Something went wrong.");
      } catch {
        setStatus("error");
        setMessage("Network error.");
      }
    },
    [email, alsoOther, audience, source]
  );

  if (status === "success") {
    return (
      <div className="flex items-center gap-3 rounded-full border border-[var(--explore-border)] bg-[var(--explore-bg-card)] px-6 py-3">
        <div className="rounded-full bg-[var(--explore-success-text)] p-1">
          <Check className="h-3.5 w-3.5 text-[var(--explore-bg)]" />
        </div>
        <span
          className="text-[var(--explore-success-text)] text-sm"
          style={SANS}
        >
          {alreadySubscribed
            ? "You\u2019re already subscribed!"
            : "You\u2019re in! Check your inbox."}
        </span>
      </div>
    );
  }

  return (
    <div style={SANS}>
      <form className="flex items-center gap-3" onSubmit={handleSubmit}>
        <input
          className="h-12 min-w-0 flex-1 rounded-full border border-[var(--explore-border)] bg-[var(--explore-bg-card)] px-5 text-[var(--explore-text-primary)] text-sm placeholder:text-[var(--explore-text-muted)] focus:border-[var(--explore-accent-yellow)] focus:outline-none focus:ring-2 focus:ring-[var(--explore-accent-yellow)]/30"
          disabled={status === "submitting"}
          onChange={(e) => {
            setEmail(e.target.value);
            if (status === "error") {
              setStatus("idle");
            }
          }}
          placeholder="Email address"
          required
          type="email"
          value={email}
        />
        <button
          className="h-12 shrink-0 cursor-pointer rounded-full px-8 font-semibold text-[var(--explore-on-accent-text)] text-sm transition-all hover:brightness-[1.08] active:scale-[0.97] disabled:cursor-not-allowed disabled:opacity-70"
          disabled={status === "submitting"}
          style={{ ...SANS, backgroundColor: BUTTON_COLOR }}
          type="submit"
        >
          {status === "submitting" ? "..." : "Subscribe"}
        </button>
      </form>
      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5">
        {/* biome-ignore lint/a11y/useSemanticElements: checkbox with ARIA */}
        <button
          aria-checked={alsoOther}
          className="flex cursor-pointer items-center gap-1.5"
          onClick={() => setAlsoOther((p) => !p)}
          role="checkbox"
          type="button"
        >
          <span
            className={`flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded border-2 transition-all ${alsoOther ? "border-[var(--explore-text-primary)] bg-[var(--explore-text-primary)]" : "border-[var(--explore-border)] bg-[var(--explore-bg-card)]"}`}
          >
            {alsoOther && (
              <Check className="h-2 w-2 text-[var(--explore-bg)]" />
            )}
          </span>
          <span className="whitespace-nowrap text-[var(--explore-text-muted)] text-xs">
            Also get {otherEditionLabel}
          </span>
        </button>
        <span className="whitespace-nowrap text-[var(--explore-text-muted)] text-xs">
          No spam · unsubscribe anytime
        </span>
      </div>
      {status === "error" && (
        <p className="mt-2 text-[var(--explore-error-text)] text-xs">
          {message}
        </p>
      )}
    </div>
  );
}

// ============================================================
// Seeded random for stable but scattered animation delays
// ============================================================

function seededRandom({ seed }: { seed: number }) {
  let s = seed;
  return () => {
    s = (s * 16_807) % 2_147_483_647;
    return (s - 1) / 2_147_483_646;
  };
}

// ============================================================
// Breathing Tag Cloud — animated categories, hover-driven tags
// ============================================================

function BreathingTagCloud({
  categories,
  tags,
  explorePathPrefix,
  tagPathPrefix,
}: {
  categories: ExploreCategory[];
  tags: TrendingTag[];
  explorePathPrefix: string;
  tagPathPrefix: string;
}) {
  const catRand = seededRandom({ seed: 42 });

  return (
    <div className="flex flex-col items-center gap-4 sm:gap-5">
      {/* Categories — with emoji, bordered */}
      <div className="flex flex-wrap justify-center gap-3 sm:gap-4">
        {categories.map((c) => {
          const duration = 14 + catRand() * 8;
          const delay = catRand() * duration;
          return (
            <Link
              className="hover:-translate-y-1 relative inline-flex shrink-0 origin-bottom items-center rounded-full border border-[var(--explore-border)] bg-[var(--explore-bg-card)] px-4 py-2 font-medium text-[var(--explore-text-primary)] text-sm no-underline shadow-[var(--explore-inline-shadow)] transition-transform duration-100 ease-out hover:z-50 hover:scale-[1.08] hover:border-[var(--explore-accent-orange)] hover:shadow-[var(--explore-shadow-hover)]"
              href={`${explorePathPrefix}/${encodeURIComponent(c.slug)}`}
              key={c.slug}
              style={{
                ...SANS,
                animation: `tag-editorial ${duration}s ease-in-out ${delay}s infinite`,
                willChange: "transform",
              }}
            >
              {c.emoji} {c.displayName}
            </Link>
          );
        })}
      </div>

      {/* Tags — hidden on mobile, all on desktop */}
      <div className="hidden">
        {tags.slice(0, 6).map((t) => (
          <Link
            className="hover:-translate-y-2 relative inline-flex shrink-0 origin-bottom items-center rounded-full border border-transparent bg-[var(--explore-tag-bg)] px-3.5 py-1.5 font-medium text-[0.8rem] text-[var(--explore-tag-text)] no-underline transition-all duration-100 ease-out hover:z-50 hover:scale-[1.16] hover:border-[var(--explore-accent-orange)] hover:bg-[var(--explore-bg-card)] hover:text-[var(--explore-text-primary)] hover:shadow-[var(--explore-shadow-hover)]"
            href={`${tagPathPrefix}/${encodeURIComponent(t.slug)}`}
            key={t.slug}
            style={{ ...SANS, willChange: "transform" }}
          >
            {t.displayName}
          </Link>
        ))}
      </div>
      <div className="hidden flex-wrap justify-center gap-2.5 sm:flex">
        {tags.map((t) => (
          <Link
            className="hover:-translate-y-2 relative inline-flex shrink-0 origin-bottom items-center rounded-full border border-transparent bg-[var(--explore-tag-bg)] px-3.5 py-1.5 font-medium text-[0.8rem] text-[var(--explore-tag-text)] no-underline transition-all duration-100 ease-out hover:z-50 hover:scale-[1.16] hover:border-[var(--explore-accent-orange)] hover:bg-[var(--explore-bg-card)] hover:text-[var(--explore-text-primary)] hover:shadow-[var(--explore-shadow-hover)]"
            href={`${tagPathPrefix}/${encodeURIComponent(t.slug)}`}
            key={t.slug}
            style={{ ...SANS, willChange: "transform" }}
          >
            {t.displayName}
          </Link>
        ))}
      </div>
    </div>
  );
}

// ============================================================
// Screenshot Card
// ============================================================

function ScreenshotCard({
  src,
  alt,
  className = "",
}: {
  src: string;
  alt: string;
  className?: string;
}) {
  return (
    /* TODO: add dark-mode screenshot variants and swap via theme */
    <div className="overflow-hidden rounded-2xl shadow-[var(--explore-card-shadow)]">
      <Image
        alt={alt}
        className={`w-full ${className}`}
        height={600}
        loading="lazy"
        src={src}
        width={500}
      />
    </div>
  );
}

// ============================================================
// Cascading Cards — overlapping card fan layout
// ============================================================

const CARD_POSITIONS = [
  {
    top: "8%",
    left: "0%",
    rotate: "-3deg",
    zIndex: 30,
    duration: "5s",
    delay: "0s",
    parallax: 0.04,
  },
  {
    top: "0%",
    left: "35%",
    rotate: "2deg",
    zIndex: 10,
    duration: "4.5s",
    delay: "1.2s",
    parallax: -0.06,
  },
  {
    top: "38%",
    left: "20%",
    rotate: "-1deg",
    zIndex: 20,
    duration: "5.5s",
    delay: "0.6s",
    parallax: 0.08,
  },
] as const;

function CascadingCards({ cards }: { cards: { src: string; alt: string }[] }) {
  return (
    <div className="relative mx-auto aspect-square w-full max-w-[22rem] sm:max-w-[26rem] lg:aspect-auto lg:h-[28rem] lg:max-w-none">
      {cards.slice(0, 3).map((card, i) => {
        const pos = CARD_POSITIONS[i];
        if (!pos) {
          return null;
        }
        return (
          <Parallax
            className="absolute w-[58%]"
            key={card.src}
            speed={pos.parallax}
            style={
              {
                "--card-rotate": pos.rotate,
                top: pos.top,
                left: pos.left,
                zIndex: pos.zIndex,
                animation: `card-float ${pos.duration} ease-in-out ${pos.delay} infinite`,
              } as React.CSSProperties
            }
          >
            {/* TODO: add dark-mode screenshot variants (e.g. engineer-story-card-1-dark.png) and swap via edition/theme */}
            <div className="overflow-hidden rounded-2xl shadow-[var(--explore-card-shadow)]">
              <Image
                alt={card.alt}
                className="w-full"
                height={400}
                loading="lazy"
                src={card.src}
                width={320}
              />
            </div>
          </Parallax>
        );
      })}
    </div>
  );
}

// ============================================================
// Illustration Break — full-width art between sections
// ============================================================

function IllustrationBreak({ src, alt }: { src: string; alt: string }) {
  return (
    <Animated>
      <Parallax
        className="mx-auto max-w-5xl px-4 pt-6 pb-2 sm:px-6 lg:pt-10 lg:pb-4"
        speed={0.05}
      >
        <div className="overflow-hidden rounded-2xl shadow-[var(--explore-card-shadow-lg)]">
          <Image
            alt={alt}
            className="w-full object-cover"
            height={400}
            loading="lazy"
            src={src}
            style={{ maxHeight: "320px" }}
            width={1200}
          />
        </div>
      </Parallax>
    </Animated>
  );
}

// ============================================================
// Mobile Phone Demo — animated scrolling phone for mobile viewport
// ============================================================

const PHONE_SCROLL_SRCS = {
  engineer: "/landing/engineer-explore-v2.webp",
  creative: "/landing/creative-explore-v2.webp",
} as const;

function MobilePhoneDemo({ edition }: { edition: Edition }) {
  return (
    <div className="flex justify-center">
      <div
        className="overflow-hidden rounded-[32px] border-[3px] border-[var(--explore-border)]"
        style={{
          width: 280,
          height: 520,
          boxShadow: "var(--explore-card-shadow-lg)",
        }}
      >
        <div className="h-full overflow-hidden">
          <div style={{ animation: "phone-scroll 30s ease-in-out infinite" }}>
            <Image
              alt="AI Primer explore feed"
              height={2456}
              loading="eager"
              sizes="308px"
              src={PHONE_SCROLL_SRCS[edition]}
              style={{ width: "110%", display: "block" }}
              width={572}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Product Showcase — desktop + mobile side by side
// ============================================================

function ProductShowcase({
  desktopSrc,
  mobileSrc,
  mobileTopOffset = "-2%",
  edition,
}: {
  desktopSrc: string;
  mobileSrc: string;
  mobileTopOffset?: string;
  edition: Edition;
}) {
  return (
    <>
      {/* Mobile: animated scrolling phone */}
      <div className="sm:hidden">
        <MobilePhoneDemo edition={edition} />
      </div>

      {/* Desktop: static screenshots with parallax */}
      <div className="relative mx-auto hidden max-w-5xl px-0 sm:block sm:px-6 sm:pb-0">
        <Parallax speed={0.06}>
          <div className="w-full drop-shadow-[var(--explore-drop-shadow)] sm:w-[82.5%]">
            <Image
              alt="AI Primer Explore page on desktop"
              className="w-full rounded-xl sm:rounded-[2rem]"
              height={900}
              loading="eager"
              sizes="(max-width: 639px) 1px, (min-width: 1280px) 845px, 82vw"
              src={desktopSrc}
              width={1440}
            />
          </div>
        </Parallax>

        <Parallax
          className="absolute right-[2%] w-[55%] sm:right-[10%] sm:w-[30.8%]"
          speed={-0.08}
          style={{ top: mobileTopOffset }}
        >
          <div className="drop-shadow-[var(--explore-drop-shadow)]">
            <Image
              alt="AI Primer Explore page on mobile"
              className="w-full rounded-2xl sm:rounded-[2rem]"
              height={844}
              loading="eager"
              sizes="(max-width: 639px) 1px, (min-width: 1280px) 315px, 30vw"
              src={mobileSrc}
              width={390}
            />
          </div>
        </Parallax>
      </div>
    </>
  );
}

// ============================================================
// Section Spacer — clean visual break between feature blocks
// ============================================================

function SectionSpacer() {
  return <div className="py-2 lg:py-4" />;
}

// ============================================================
// Divider
// ============================================================

function Divider() {
  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6">
      <div className="border-[var(--explore-border)] border-t" />
    </div>
  );
}

// ============================================================
// Main Component
// ============================================================

export function UnifiedLandingPage(props: UnifiedLandingPageProps) {
  const [edition, setEdition] = useState<Edition>(
    props.defaultEdition ?? "engineer"
  );
  const router = useRouter();
  const pathname = usePathname();
  const configs = useMemo(() => buildEditionConfigs(props), [props]);
  const current = configs[edition];
  const otherLabel = edition === "engineer" ? "Creator" : "Engineer";

  const handleEditionToggle = useCallback(
    (ed: Edition) => {
      setEdition(ed);
      window.scrollTo({ top: 0, behavior: "smooth" });

      // Navigate to correct route so metadata, history, and URL stay in sync
      if (pathname.startsWith("/landing/")) {
        const editionSlug = ed === "engineer" ? "engineers" : "creatives";
        router.replace(`/landing/${editionSlug}`);
      } else if (
        pathname === "/for-engineers" ||
        pathname === "/for-creatives"
      ) {
        const target = ed === "engineer" ? "/for-engineers" : "/for-creatives";
        router.replace(target);
      }
    },
    [pathname, router]
  );

  return (
    <div
      className="flex min-h-screen flex-col"
      style={{ backgroundColor: "var(--explore-bg)" }}
    >
      <style>{GLOBAL_KEYFRAMES}</style>

      {/* -------------------------------------------------------- */}
      {/* Header                                                   */}
      {/* -------------------------------------------------------- */}
      <header className="sticky top-0 z-50 bg-[var(--explore-bg)]/95 backdrop-blur-sm">
        <div className="relative mx-auto flex h-14 max-w-5xl items-center px-4 sm:px-6">
          <Link className="flex items-center gap-2.5" href="/">
            <Image
              alt="AI Primer"
              className="rounded-full"
              height={36}
              src="/logo.webp"
              width={36}
            />
            <span
              className="font-semibold text-[var(--explore-text-primary)] text-base"
              style={SANS}
            >
              AI Primer
            </span>
          </Link>

          {/* Edition toggle — absolutely centered on desktop, below logo row on mobile */}
          <div className="pointer-events-none absolute inset-0 hidden items-center justify-center sm:flex">
            <div className="pointer-events-auto">
              <EditionToggle edition={edition} onToggle={handleEditionToggle} />
            </div>
          </div>

          <nav className="ml-auto flex items-center gap-4" style={SANS}>
            <Link
              className="hidden font-medium text-[var(--explore-text-primary)] text-sm hover:text-[var(--explore-text-muted)] sm:block"
              href={current.exploreHubPath}
            >
              Explore
            </Link>
          </nav>
        </div>
        {/* Mobile toggle — below header row */}
        <div className="flex justify-center pb-2 sm:hidden">
          <EditionToggle edition={edition} onToggle={handleEditionToggle} />
        </div>
      </header>

      <main className="flex-1">
        {/* -------------------------------------------------------- */}
        {/* HERO — toggle + headline + CTA to explore               */}
        {/* -------------------------------------------------------- */}
        <section className="px-4 pt-6 pb-6 sm:px-6 sm:pt-8 sm:pb-8 lg:pt-10 lg:pb-10">
          <div className="mx-auto max-w-5xl">
            <div className="mx-auto max-w-3xl text-center">
              <h1
                className="mb-3 text-balance font-bold text-3xl text-[var(--explore-landing-headline)] leading-[1.1] tracking-tight transition-all duration-500 sm:text-4xl lg:text-5xl"
                style={TITLE}
              >
                {current.headline}
              </h1>

              <p
                className="mx-auto mb-5 max-w-lg text-pretty text-[var(--explore-text-secondary)] text-sm leading-relaxed transition-all duration-500 sm:text-base"
                style={SANS}
              >
                {current.subtitle}
              </p>
            </div>

            <div className="mt-6 sm:mt-8 lg:mt-10">
              <ProductShowcase
                desktopSrc={current.showcaseDesktop}
                edition={edition}
                mobileSrc={current.showcaseMobile}
                mobileTopOffset={edition === "engineer" ? "-5%" : "-2%"}
              />
            </div>

            <div className="mx-auto mt-8 max-w-3xl text-center sm:mt-10">
              <Link
                className="inline-flex items-center gap-2 rounded-full px-8 py-3 font-semibold text-[var(--explore-on-accent-text)] text-base transition-all hover:brightness-[1.08] active:scale-[0.97]"
                href={current.exploreHubPath}
                style={{
                  ...SANS,
                  backgroundColor: BUTTON_COLOR,
                  animation: "btn-glow 2s ease-in-out infinite",
                }}
              >
                Explore now
                <ArrowRight className="h-4 w-4" />
              </Link>

              <div
                className="mt-10 grid grid-cols-3 gap-4 sm:mt-12"
                style={SANS}
              >
                {[
                  { value: "500+", label: "Sources" },
                  { value: "6hrs", label: "Ahead of mainstream" },
                  { value: "8am", label: "Updated daily" },
                ].map((s) => (
                  <div className="text-center" key={s.label}>
                    <p
                      className="font-bold text-[var(--explore-text-primary)] text-xl tracking-tight sm:text-2xl"
                      style={TITLE}
                    >
                      {s.value}
                    </p>
                    <p className="text-[0.65rem] text-[var(--explore-text-muted)] sm:text-xs">
                      {s.label}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <Divider />

        {/* -------------------------------------------------------- */}
        {/* Tag Cloud — "Everything AI, organized your way"          */}
        {/* -------------------------------------------------------- */}
        <Animated className="py-10 lg:py-16">
          <div className="mx-auto max-w-5xl px-4 sm:px-6">
            <div className="mb-6 text-center lg:mb-8">
              <p
                className="mb-2 font-semibold text-xs uppercase tracking-[0.15em]"
                style={{ ...SANS, color: ACCENT }}
              >
                Smart filtering
              </p>
              <h2
                className="mb-2 text-balance font-bold text-2xl text-[var(--explore-text-primary)] sm:text-3xl"
                style={TITLE}
              >
                Everything AI, organized your&nbsp;way
              </h2>
              <p
                className="mx-auto max-w-md text-[var(--explore-text-secondary)] text-sm sm:text-base"
                style={SANS}
              >
                {current.data.categories.length} categories,{" "}
                {current.data.allTags.length} topics and tools
              </p>
            </div>
            <BreathingTagCloud
              categories={current.data.categories}
              explorePathPrefix={current.explorePathPrefix}
              tagPathPrefix={current.tagPathPrefix}
              tags={current.data.allTags}
            />
          </div>
        </Animated>

        {/* -------------------------------------------------------- */}
        {/* Illustration Break 1 — bookstore                         */}
        {/* -------------------------------------------------------- */}
        <IllustrationBreak
          alt="Editorial illustration of a London bookstore"
          src="/landing/illustration-bookstore.webp"
        />

        {/* -------------------------------------------------------- */}
        {/* Feature 1: Daily Stories                                 */}
        {/* -------------------------------------------------------- */}
        <Animated className="pt-14 pb-20 lg:pt-20 lg:pb-28">
          <div className="mx-auto max-w-5xl px-4 sm:px-6">
            <div className="flex flex-col gap-8 lg:flex-row lg:items-center lg:gap-12">
              {/* Text */}
              <div className="lg:w-2/5">
                <p
                  className="mb-2 font-semibold text-xs uppercase tracking-[0.15em]"
                  style={{ ...SANS, color: ACCENT }}
                >
                  Daily stories
                </p>
                <h2
                  className="mb-3 text-balance font-bold text-2xl text-[var(--explore-text-primary)] leading-snug sm:text-2xl lg:text-3xl"
                  style={TITLE}
                >
                  5&nbsp;stories a day, researched and&nbsp;opinionated
                </h2>
                <p
                  className="text-pretty text-[var(--explore-text-secondary)] text-base leading-relaxed sm:text-base"
                  style={SANS}
                >
                  <span style={HIGHLIGHT_YELLOW}>Model releases</span>,{" "}
                  <span style={HIGHLIGHT_PEACH}>agent tooling</span>,{" "}
                  <span style={HIGHLIGHT_MINT}>workflow patterns</span>, and the
                  things other engineers are building. Each story is drafted by
                  an AI agent that does its own web research, embeds the
                  evidence, and has&nbsp;opinions.
                </p>
              </div>

              {/* Story cards — cascading fan */}
              <div className="lg:w-3/5">
                <CascadingCards cards={current.storyCards} />
              </div>
            </div>
          </div>
        </Animated>

        {/* -------------------------------------------------------- */}
        {/* Feature 2: Deep Dive — story detail view                 */}
        {/* -------------------------------------------------------- */}
        <Animated className="py-14 lg:py-20">
          <div className="mx-auto max-w-5xl px-4 sm:px-6">
            {/* Text — full width so heading stays on 2 lines */}
            <div className="mb-6 text-center lg:mb-8">
              <p
                className="mb-2 font-semibold text-xs uppercase tracking-[0.15em]"
                style={{ ...SANS, color: ACCENT }}
              >
                Briefs
              </p>
              <h2
                className="mb-3 font-bold text-[var(--explore-text-primary)] text-xl leading-snug sm:text-2xl lg:text-3xl"
                style={TITLE}
              >
                30+&nbsp;briefs for the long&nbsp;tail
              </h2>
              <p
                className="mx-auto max-w-2xl text-pretty text-[var(--explore-text-secondary)] text-sm leading-relaxed sm:text-base"
                style={SANS}
              >
                The <span style={HIGHLIGHT_YELLOW}>small tool release</span>,
                the <span style={HIGHLIGHT_MINT}>Reddit thread</span> with a
                clever workflow, the{" "}
                <span style={HIGHLIGHT_PEACH}>model update</span> you would have
                missed. Everything interesting that did not make it into
                a&nbsp;story.
              </p>
            </div>

            {/* Detail view screenshot */}
            <div className="mx-auto max-w-3xl">
              <ScreenshotCard
                alt="Story detail view with prompts and links"
                src={current.showcaseDetail}
              />
            </div>
          </div>
        </Animated>

        <SectionSpacer />

        {/* -------------------------------------------------------- */}
        {/* Philosophy: "No FOMO, just play"                        */}
        {/* -------------------------------------------------------- */}
        <Animated>
          <section
            className="py-16 lg:py-24"
            style={{ backgroundColor: "var(--explore-bg-card)" }}
          >
            <div className="mx-auto max-w-3xl px-4 text-center sm:px-6">
              <h2
                className="mb-4 text-balance font-bold text-2xl text-[var(--explore-text-primary)] sm:text-3xl lg:text-4xl"
                style={TITLE}
              >
                One scan, every&nbsp;morning
              </h2>
              <p
                className="mx-auto mb-8 max-w-lg text-pretty text-[var(--explore-text-secondary)] text-base leading-relaxed sm:text-lg"
                style={SANS}
              >
                Instead of 30 minutes on X, Reddit, and Hacker News, you get one
                daily scan. Stories for the big stuff. Briefs for everything
                else. Searchable, filterable, and built for people who are
                already deep in&nbsp;AI.
              </p>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                {[
                  { emoji: "\uD83D\uDCE1", label: "500+ sources" },
                  { emoji: "\uD83D\uDCDD", label: "5 stories" },
                  { emoji: "\u26A1", label: "30+ briefs" },
                  { emoji: "\uD83D\uDD0D", label: "Searchable" },
                ].map((v) => (
                  <div
                    className="rounded-xl bg-[var(--explore-bg-card)]/60 p-4 shadow-sm"
                    key={v.label}
                  >
                    <span className="mb-1 block text-2xl">{v.emoji}</span>
                    <span
                      className="font-semibold text-[var(--explore-text-primary)] text-sm"
                      style={SANS}
                    >
                      {v.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </Animated>

        <SectionSpacer />

        {/* -------------------------------------------------------- */}
        {/* Email: "Also in your inbox"                             */}
        {/* -------------------------------------------------------- */}
        <Animated className="py-14 lg:py-20">
          <div className="mx-auto max-w-5xl px-4 sm:px-6">
            <div className="flex flex-col-reverse items-center gap-8 lg:flex-row lg:items-center lg:gap-12">
              {/* Text + subscribe */}
              <div className="lg:sticky lg:top-20 lg:w-2/5">
                <p
                  className="mb-2 font-semibold text-xs uppercase tracking-[0.15em]"
                  style={{ ...SANS, color: ACCENT }}
                >
                  Daily briefing
                </p>
                <h2
                  className="mb-3 text-balance font-bold text-2xl text-[var(--explore-text-primary)] sm:text-3xl"
                  style={TITLE}
                >
                  Also in your&nbsp;inbox
                </h2>
                <p
                  className="mb-6 text-pretty text-[var(--explore-text-secondary)] text-sm leading-relaxed sm:text-base"
                  style={SANS}
                >
                  Every morning: 5 stories and 30+ briefs covering what shipped,
                  what people are building, and the tools worth trying. Scan it
                  in{" "}
                  <strong className="text-[var(--explore-text-primary)]">
                    5 minutes
                  </strong>
                  .
                </p>
                <SubscribeForm
                  audience={current.subscribeAudience}
                  key={edition}
                  otherEditionLabel={otherLabel}
                  source={`unified-landing-email-${edition}`}
                />
              </div>

              {/* Email screenshot with inbox header */}
              <Parallax className="w-full lg:w-3/5" speed={0.05}>
                <div className="overflow-hidden rounded-xl border border-[var(--explore-border-light)] shadow-[var(--explore-card-shadow-lg)]">
                  {/* Fake inbox bar */}
                  <div
                    className="flex items-center gap-2.5 border-[var(--explore-border-light)] border-b bg-[var(--explore-bg-card)] px-4 py-3"
                    style={SANS}
                  >
                    <Mail className="h-4 w-4 shrink-0 text-[var(--explore-text-muted)]" />
                    <span className="truncate font-medium text-[var(--explore-text-primary)] text-sm">
                      AI Primer Daily Briefing
                    </span>
                    <span className="ml-auto shrink-0 text-[var(--explore-text-muted)] text-xs">
                      8:00 AM
                    </span>
                  </div>
                  <Image
                    alt={`AI Primer daily email briefing for ${edition}s`}
                    className="w-full"
                    height={1200}
                    src={current.emailPreview}
                    width={600}
                  />
                </div>
              </Parallax>
            </div>
          </div>
        </Animated>

        {/* Illustration — bottom art before footer */}
        <IllustrationBreak
          alt="Creator working at desk on a city balcony at twilight"
          src="/landing/illustration-balcony.webp"
        />
      </main>

      {/* Footer — tight to illustration */}
      <footer>
        <div className="mx-auto max-w-5xl px-4 pt-0 pb-8 sm:px-6">
          <div
            className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center"
            style={SANS}
          >
            <div className="flex items-center gap-2.5">
              <Image
                alt="AI Primer"
                className="rounded-full"
                height={28}
                src="/logo.webp"
                width={28}
              />
              <span className="text-[var(--explore-text-muted)] text-xs">
                Your daily dose of AI. Gently, only what matters.
              </span>
            </div>
            <div className="flex items-center gap-4">
              <Link
                className="flex items-center gap-1 text-[var(--explore-text-muted)] text-xs hover:text-[var(--explore-text-primary)]"
                href={X_LINK}
                rel="noopener noreferrer"
                target="_blank"
              >
                <svg
                  className="h-3.5 w-3.5"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
              </Link>
              <Link
                className="text-[var(--explore-text-muted)] text-xs hover:text-[var(--explore-text-primary)]"
                href={TELEGRAM_LINK}
                rel="noopener noreferrer"
                target="_blank"
              >
                Telegram
              </Link>
              <Link
                className="text-[var(--explore-text-muted)] text-xs hover:text-[var(--explore-text-primary)]"
                href={current.exploreHubPath}
              >
                Explore
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
